import React, { useEffect, useMemo, useState } from 'react';
import { cadenceApi, TOUCHPOINT_URL } from '../services/api';
import { BoardCell, BoardCellState, BoardRow, Cadence, WhatsAppTemplate } from '../types';
import { STAGE_LABEL, fmtDay, fmtDayTime, fmtDwell, stageCls } from '../lib';
import { Failed, Loading, Section } from './PlatformBits';
import { Pager } from './SendCells';

/** The delivery board (MAG-194): one row per lead in order of attachment,
 *  oldest first, the cadence's steps across as dots. A dot is Meta's answer
 *  for that step (read / delivered / sent / failed) or where the clock stands
 *  (waiting / held / stopped). The two right-hand columns are the lead's
 *  lifetime opens and time on the album; hovering the opens lists each one
 *  with the source it carried. No open is credited to a step: a cadence
 *  step's link carries no step tag (MAG-195), so the board says only what
 *  the event says. */

const PAGE = 200;

const DOT: Record<BoardCellState, string> = {
  read: 'bg-forest',
  delivered: 'bg-[#6F9C6B]',
  sent: 'bg-sage',
  failed: 'bg-brick',
  sending: 'border-[1.5px] border-sage',
  skipped: 'border border-dashed border-charcoal/40',
  held: 'border-[1.5px] border-gold',
  waiting: 'border-[1.5px] border-charcoal/25',
  stopped: 'border border-charcoal/10 opacity-60',
};
const LABEL: Record<BoardCellState, string> = {
  read: 'Read', delivered: 'Delivered', sent: 'Sent, not yet delivered', failed: 'Failed', sending: 'Sending now',
  skipped: 'Skipped', held: 'Held', waiting: 'Not yet sent', stopped: 'Ended before this step',
};
const WHEN: Partial<Record<BoardCellState, string>> = { read: 'read', delivered: 'delivered', sent: 'sent', failed: 'failed', waiting: 'due', held: 'held until', skipped: 'skipped' };
const BAR: BoardCellState[] = ['waiting', 'sent', 'delivered', 'read', 'failed'];

const stepLabel = (c: Cadence['steps'][number]) =>
  `T+${c.offset_days}d${c.at_time ? ` ${c.at_time.slice(0, 5)}` : c.offset_hours ? ` ${c.offset_hours}h` : ''}`;

/** Every open the lead made, one per line, as the event recorded it. */
const opensTitle = (row: BoardRow): string =>
  row.opens_at.map((o) => `${fmtDayTime(o.at)} · ${o.page === 'project_album' ? 'album' : o.page} · ${o.src || 'untagged'}`).join('\n')
  + (row.opens > row.opens_at.length ? `\n… and ${row.opens - row.opens_at.length} more` : '');

interface Hover { row: BoardRow; cell: BoardCell; x: number; y: number; }

const CadenceBoard: React.FC<{ cadence: Cadence; templateById: Record<string, WhatsAppTemplate> }> = ({ cadence, templateById }) => {
  const [board, setBoard] = useState<{ total: number; rows: BoardRow[] } | null>(null);
  const [skip, setSkip] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [hover, setHover] = useState<Hover | null>(null);

  useEffect(() => { setBoard(null); setSkip(0); }, [cadence.id]);
  useEffect(() => {
    cadenceApi.board(cadence.id, skip, PAGE).then(setBoard)
      .catch((err) => setError(err?.response?.data?.detail || err.message));
  }, [cadence.id, skip]);

  const steps = cadence.steps;
  const rows = useMemo(() => board?.rows || [], [board]);

  // Per step on this page: how many sit in each state.
  const perStep = useMemo(() => steps.map((_, k) => {
    const c: Record<string, number> = {};
    for (const r of rows) { const st = r.cells[k]?.state; if (st) c[st] = (c[st] || 0) + 1; }
    return c;
  }), [rows, steps]);
  const opened = rows.filter((r) => r.opens > 0).length;
  const opens = rows.reduce((n, r) => n + r.opens, 0);
  const dwell = rows.reduce((n, r) => n + r.dwell_ms, 0);

  if (error) return <Failed what="the cadence board" error={error} />;

  const cols = `72px minmax(220px, max-content) 96px repeat(${steps.length}, 26px) 110px 70px 1fr`;
  let prevDay = '';

  return (
    <Section title="Delivery board"
             note="one row per lead in order of attachment · a dot per step · hover the opens for where each came from">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-1 py-3 text-[12px] text-graphite">
        {(['read', 'delivered', 'sent', 'failed', 'waiting', 'held', 'stopped'] as BoardCellState[]).map((st) => (
          <span key={st} className="inline-flex items-center gap-1.5"><i className={`inline-block w-[9px] h-[9px] rounded-full ${DOT[st]}`} />{LABEL[st].toLowerCase()}</span>
        ))}
      </div>
      {!board && <Loading what="the board" />}
      {board && (
        <div className="min-w-[900px] grid items-center" style={{ gridTemplateColumns: cols }}>
          {/* header */}
          {['Attached', 'Lead', 'Stage'].map((h) => <div key={h} className="text-[11px] uppercase tracking-[0.06em] font-semibold text-graphite self-end pb-1">{h}</div>)}
          {steps.map((s, k) => (
            <div key={s.id} className="text-[9.5px] text-graphite text-center self-end leading-tight pb-1 num" title={`${stepLabel(s)} · ${templateById[s.template_id]?.display_name || s.template_id}`}>
              <b className="block text-[11px] text-charcoal">{k + 1}</b>d{s.offset_days}
            </div>
          ))}
          <div className="text-[11px] uppercase tracking-[0.06em] font-semibold text-graphite self-end pb-1 pl-3">Opens</div>
          <div className="text-[11px] uppercase tracking-[0.06em] font-semibold text-graphite self-end pb-1 pl-3">Time</div>
          <div />
          {/* per-step summary */}
          <div className="text-[11px] text-graphite self-end pb-3 num">{board.total} leads</div>
          <div className="text-[11px] text-graphite self-end pb-3 num">{cadence.enrolled} in the cadence now</div>
          <div />
          {perStep.map((c, k) => {
            const reached = (c.read || 0) + (c.delivered || 0) + (c.sent || 0);
            return (
              <div key={steps[k].id} className="px-[2px] pb-2 self-start">
                <div className="flex flex-col justify-end gap-px h-[34px]">
                  {BAR.map((st) => c[st] ? <i key={st} className={`block w-full rounded-[1px] ${DOT[st]}`} style={{ height: Math.max(1, (c[st] / rows.length) * 34) }} /> : null)}
                </div>
                <div className="text-center text-[10px] text-graphite num mt-0.5 leading-tight">{reached || ''}<span className="block text-brick h-3">{c.failed || ''}</span></div>
              </div>
            );
          })}
          <div className="text-[11px] text-graphite self-end pb-3 pl-3 num whitespace-nowrap">{opened} opened · {opens}</div>
          <div className="text-[11px] text-graphite self-end pb-3 pl-3 num whitespace-nowrap">{fmtDwell(dwell)}</div>
          <div className="text-[10px] text-graphite self-end pb-3 pl-4 leading-tight">reached<span className="block text-brick">failed</span></div>
          <div className="col-span-full h-px bg-charcoal/10 mb-1" />
          {/* rows */}
          {rows.length === 0 && <div className="col-span-full text-center text-graphite text-[13px] py-10">Nobody has been attached to this cadence.</div>}
          {rows.map((r) => {
            const day = fmtDay(r.anchor_at);
            const showDay = day !== prevDay; prevDay = day;
            return (
              <div key={r.enrollment_id} className="contents group">
                <div className={`h-6 leading-6 text-[11px] num pr-2 ${showDay ? 'text-charcoal font-semibold' : 'text-transparent'} group-hover:bg-cream`}>{day}</div>
                <div className="h-6 leading-6 text-[12.5px] whitespace-nowrap pr-4 group-hover:bg-cream">
                  <a href={`${TOUCHPOINT_URL}/leads/${r.lead_id}`} target="_blank" rel="noreferrer" className="font-semibold text-charcoal hover:text-forest hover:underline underline-offset-2" title={r.lead_name || undefined}>{r.lead_name || 'unnamed'}</a>
                  <span className="text-[11px] text-graphite num ml-1.5">{r.lead_phone}</span>
                </div>
                <div className="h-6 flex items-center pr-2 group-hover:bg-cream">
                  {r.stage && <span className={`inline-block text-[10.5px] font-medium px-1.5 py-px rounded-full border whitespace-nowrap ${stageCls(r.stage)}`}>{STAGE_LABEL[r.stage] ?? r.stage}</span>}
                </div>
                {r.cells.map((c, k) => (
                  <div key={c.step_id} className={`relative h-6 flex items-center justify-center group-hover:bg-cream ${c.state === 'stopped' ? '' : 'before:absolute before:left-0 before:right-0 before:top-1/2 before:h-px before:bg-charcoal/[0.08]'} ${k === 0 ? 'before:left-1/2' : ''} ${k === r.cells.length - 1 ? 'before:right-1/2' : ''}`}>
                    <span className={`relative z-[1] block w-[9px] h-[9px] rounded-full cursor-pointer transition-transform hover:scale-150 ${DOT[c.state]}`}
                          onMouseEnter={(e) => setHover({ row: r, cell: c, x: e.clientX, y: e.clientY })}
                          onMouseMove={(e) => setHover((h) => h && { ...h, x: e.clientX, y: e.clientY })}
                          onMouseLeave={() => setHover(null)} />
                  </div>
                ))}
                <div className={`h-6 leading-6 pl-3 text-[12px] num whitespace-nowrap group-hover:bg-cream ${r.opens ? '' : 'text-charcoal/30'}`}
                     title={r.opens ? opensTitle(r) : undefined}>
                  {r.opens ? <><b className="font-semibold">{r.opens}</b><span className="text-[11px] text-graphite ml-1">{r.album_opens && r.website_opens ? `${r.album_opens}a · ${r.website_opens}w` : r.website_opens && !r.album_opens ? 'web' : 'album'}</span></> : '—'}
                </div>
                <div className={`h-6 leading-6 pl-3 text-[12px] num whitespace-nowrap group-hover:bg-cream ${r.dwell_ms ? '' : 'text-charcoal/30'}`}>{fmtDwell(r.dwell_ms)}</div>
                <div className="h-6 group-hover:bg-cream" />
              </div>
            );
          })}
        </div>
      )}
      {board && board.total > PAGE && <Pager skip={skip} limit={PAGE} total={board.total} onSkip={setSkip} />}
      {hover && (
        <div className="fixed z-30 pointer-events-none bg-charcoal text-warm-white rounded-md px-2.5 py-1.5 text-[12px] leading-snug shadow-lg max-w-[280px]"
             style={{ left: Math.min(hover.x + 14, window.innerWidth - 300), top: hover.y - 64 < 8 ? hover.y + 16 : hover.y - 64 }}>
          <b className="font-semibold">{hover.row.lead_name || 'unnamed'}</b> <span className="opacity-70 num">{hover.row.lead_phone}</span>
          <div className="opacity-70">step {hover.cell.position + 1} · {templateById[steps[hover.cell.position]?.template_id]?.display_name || 'template'}</div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <i className={`inline-block w-[8px] h-[8px] rounded-full ${DOT[hover.cell.state]}`} />{LABEL[hover.cell.state]}
            {hover.cell.state === 'stopped' && hover.row.ended_reason && <span className="opacity-70">· {hover.row.ended_reason} {hover.row.ended_at ? fmtDay(hover.row.ended_at) : ''}</span>}
            {hover.cell.at && WHEN[hover.cell.state] && <span className="opacity-70 num">· {WHEN[hover.cell.state]} {fmtDayTime(hover.cell.at)}</span>}
            {hover.cell.error_code && <span className="opacity-70">· {hover.cell.error_code}</span>}
          </div>
        </div>
      )}
    </Section>
  );
};

export default CadenceBoard;
